<script setup lang="ts">
  import { computed, h, ref } from "vue";
  import type { VNode, VNodeChild } from "vue";
  import {
    NGradientText,
    type DropdownOption,
    type MenuOption,
  } from "naive-ui";
  import { useAuthStore } from "@/store/modules/auth";
  import { useRouterPush } from "@/hooks/common/router";
  import { useSvgIcon } from "@/hooks/common/icon";
  import { $t } from "@/locales";
  import { useUserStore } from "@/store/modules/user";
  import { logoutUser } from "@/service/api";
  import { clearAuthStorage } from "@/store/modules/auth/shared";
  import Feedback from "@/components/common/feed-back.vue";

  defineOptions({
    name: "UserAvatar",
  });

  // const appStore = useAppStore();
  const authStore = useAuthStore();
  const userStore = useUserStore();
  const isLogin = computed(() => userStore.isUserAuthenticated());
  const username = computed(() => userStore.userInfo.username);
  const { routerPushByKey } = useRouterPush();
  const { SvgIconVNode } = useSvgIcon();
  const showFeedback = ref(false);
  interface Props {
    /** Whether to show the title */
    showTitle?: boolean;
    direction: string;
  }

  const props = defineProps<Props>();

  function loginOrRegister() {
    window.location.href = "/chat?redirect=/platform/home&show=login";
  }

  type DropdownKey = "logout" | "chat" | "feedback" | "profile";

  // Define our custom option type based on DropdownOption
  type CustomDropdownOption = {
    key: DropdownKey;
    label: string | (() => VNodeChild);
    icon?: () => VNode;
    href?: string;
    type?: "divider";
  };

  const options = computed<DropdownOption[]>(() => {
    const opts: DropdownOption[] = [
      {
        label: username.value,
        key: "profile",
        icon: SvgIconVNode({ icon: "ph:user", fontSize: 18 }),
      },

      {
        label: $t("common.backToChat"),
        key: "chat",
        icon: SvgIconVNode({ icon: "ph:chat-centered-dots", fontSize: 18 }),
        href: "/chat",
      },
      {
        label: $t("feedback.sendFeedback"),
        key: "feedback",
        icon: SvgIconVNode({ icon: "ph:seal-warning-bold", fontSize: 18 }),
      },
      {
        type: "divider",
        key: "divider",
      },
      {
        label: $t("common.logout"),
        key: "logout",
        icon: SvgIconVNode({ icon: "ph:sign-out", fontSize: 18 }),
      },
    ];

    return opts;
  });

  const renderDropdownLabel = (option: MenuOption) => {
    if ("type" in option && option.type === "divider") {
      return null;
    }

    // Type guard for our custom options
    const isCustomOption = (opt: MenuOption): opt is CustomDropdownOption =>
      "key" in opt &&
      typeof opt.key === "string" &&
      ["logout", "chat", "feedback", "profile"].includes(opt.key as string);

    if (!isCustomOption(option)) {
      return null;
    }

    if (!option.href) {
      return h(
        "span",
        {
          onClick: () => handleDropdown(option.key),
        },
        [h("span", { class: "ml-2" }, option.label)]
      );
    }

    const handleClick = (e: MouseEvent) => {
      e.preventDefault();
      handleDropdown(option.key);
    };

    return h(
      "a",
      {
        href: option.href,
        onClick: handleClick,
        style: "text-decoration: none; color: inherit;",
      },
      [h("span", { class: "ml-2" }, option.label)]
    );
  };

  function handleDropdown(key: DropdownKey) {
    if (key === "logout") {
      logout();
    } else if (key === "chat") {
      window.location.href = "/chat";
    } else if (key === "feedback") {
      showFeedback.value = true;
    } else if (key === "profile") {
      // Profile is just a display item, no action needed
    } else {
      routerPushByKey(key);
    }
  }

  async function logout() {
    window.$dialog?.info({
      transformOrigin: "center",
      // title: $t('common.tip'),
      class:
        "rounded-lg bg-white text-right shadow-lg dark:bg-gray-700 dark:text-gray-200",
      actionStyle: "justify-content: space-around",
      content: $t("common.logoutConfirm"),
      positiveText: $t("common.confirm"),
      negativeText: $t("common.cancel"),
      onPositiveClick: () => {
        logoutUser();
        userStore.resetUserInfo();
        authStore.resetStore();
        clearAuthStorage();
        window.location.href = "/chat?redirect=/platform/home&show=login";
      },
    });
  }
</script>

<template>
  <div>
    <template v-if="!isLogin">
      <NButton
        v-if="props.showTitle"
        secondary
        block
        @click="loginOrRegister"
        class="login-wrapper"
      >
        {{ $t("page.login.common.loginOrRegister") }}
      </NButton>
      <NButton
        v-else
        secondary
        block
        @click="loginOrRegister"
        class="min-w-[3.5rem]"
      >
        <NIcon>
          <LoginIcon />
        </NIcon>
      </NButton>
    </template>

    <NDropdown
      v-else
      placement="bottom"
      trigger="click"
      size="huge"
      :style="{ direction: props.direction }"
      :options="options"
      :render-label="renderDropdownLabel"
      @select="handleDropdown"
    >
      <div>
        <ButtonIcon class="flex w-full justify-between">
          <SvgIcon icon="ph:user-circle" class="text-icon-large" />
          <NTooltip
            v-if="props.showTitle"
            :disabled="!username || username.length <= 16"
            trigger="hover"
            placement="left-end"
          >
            <template #trigger>
              <div
                class="username-display"
                :class="{ 'text-right': props.direction === 'rtl' }"
              >
                <NGradientText
                  :font-size="18"
                  type="info"
                  class="truncated-text ltr-text"
                >
                  {{ username }}
                </NGradientText>
              </div>
            </template>
            <span>{{ username }}</span>
          </NTooltip>
        </ButtonIcon>
      </div>
    </NDropdown>
    <Feedback v-model:visible="showFeedback" :direction="direction" />
  </div>
</template>

<style scoped lang="css">
  .login-wrapper {
    width: 14.5rem;
  }
  .username-display {
    min-width: 10.5rem;
    max-width: 150px;
    overflow: hidden;
    display: inline-block;
    vertical-align: middle;
    font-size: 0.75em;
    text-align: left;
  }

  .truncated-text {
    display: block;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Ensure the NGradientText component respects the container width */
  .username-display :deep(.n-gradient-text) {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
  }

  /* Force LTR direction for username text regardless of layout direction */
  .ltr-text {
    direction: ltr;
    text-align: left;
  }

  .username-display :deep(.ltr-text) {
    direction: ltr;
    text-align: left;
  }
</style>
